/**
 * Abstraction around JavaScript's setTimeout() function. Handles details
 * like making sure timeouts still work even if they are days in the future.
 * It is fulfilled when the timeout passes, failing it will clear the timeout.
 * @constructor
 * @extends {Foundation.Promise}
 * @param {number} milliseconds Timeout after this many milliseconds.
 */
Foundation.Timer = function (milliseconds) {
    Foundation.Promise.call(this);
        
    /**
     * When this timer is supposed to fire.
     * @protected
     */
    this.firesAt = milliseconds + (new Date()).getTime();
    
    // Allow for negative values.
    if (milliseconds < 0) {
        this.fulfill();
        return;
    }
    
    // Initialize the timer.
    this.setTimer(milliseconds);
        
    // Ensure a bit of cleanup.
    this.always(this.clearTimer.bind(this));
};
Foundation.inherit(Foundation.Timer, Foundation.Promise);


/**
 * Cancel a timer. Fails the promise if it hasn't already completed.
 */
Foundation.Timer.prototype.cancel = Foundation.Timer.prototype.tryFail;

/**
 * Maximum number of milliseconds for which we're willing to use a simple
 * setTimeout().
 * @const
 */
Foundation.Timer.INTERVAL_THRESHOLD = 59 * 1000;

/**
 * Set up the timer.
 * @private
 * @param {number} milliseconds
 */
Foundation.Timer.prototype.setTimer = function (milliseconds) {
    if (milliseconds < Foundation.Timer.INTERVAL_THRESHOLD) {
        this.clearTimer();
        this.timeout = setTimeout(this.fulfill.bind(this), milliseconds);
    }
    else {
        // For longer timeouts, we want to be sure we don't run into issues
        // with browsers not expecting long timeouts.
        Foundation.Timer.setUpIntervalTimer(this);
    }
};

/**
 * @private
 */
Foundation.Timer.prototype.clearTimer = function () {
    if (this.timeout) {
        clearInterval(this.timeout);
        this.timeout = null;
    }
};

/**
 * Global for storing timers that surpass the INTERVAL_THRESHOLD. Ought to
 * be implemented using a real heap, but just keeping it sorted is probably
 * good enough for now.
 * @type {Array.<Foundation.Timer>}
 * @private
 */
Foundation.Timer.timers = [];

/**
 * Global for the interval timer.
 */
Foundation.Timer.interval = null;

/**
 * Queue up a timer that is meant to fire a while from now.
 * @private
 * @param {Foundation.Timer} timeout
 */
Foundation.Timer.setUpIntervalTimer = function (timeout) {
    // Insert timeout into the global array, making sure the next timeout is
    // always at the start of the array.
    Foundation.Timer.timers.push(timeout);
    Foundation.Timer.timers.sort(function (a, b) {
        if (a.firesAt < b.firesAt) return -1;
        if (a.firesAt > b.firesAt) return 1;
        return 0;
    });
    
    // Set up the interval handler.
    if (Foundation.Timer.interval === null) {
        Foundation.Timer.interval = setInterval(function () {
            var now = (new Date()).getTime();
            var nextInterval = now + Foundation.Timer.INTERVAL_THRESHOLD;
            
            while (
                   Foundation.Timer.timers.length
                && Foundation.Timer.timers[0].firesAt < nextInterval
            ) {
                // One way or another, this timer is no longer needed in the 
                // extended interval array, so we can remove it from the global
                // array.
                var timer = Foundation.Timer.timers.shift();
                
                // Timer has been canceled, skip.
                if (timer.isFailed()) continue;
                
                // When is this timer meant to fire?
                var millisecondsFromNow = timer.firesAt - now;
                
                if (now >= timer.firesAt) {
                    // Timer should have already fired, so fulfill the promise already.
                    timer.fulfill();
                }
                else {
                    // Timer is supposed to fire soon, schedule it.
                    timer.setTimer(millisecondsFromNow);
                }
            }
            
            if (Foundation.Timer.timers.length === 0) {
                clearInterval(Foundation.Timer.interval);
                Foundation.Timer.interval = null;
            }
        }, Foundation.Timer.INTERVAL_THRESHOLD);
    }
};

/**
 * Returns a promise that will be fulfilled shortly in the future. Similar
 * to setTimeout(callback, 1), but will only ever set one timeout.
 * @return {Foundation.Promise}
 */
Foundation.Timer.deferral = function () {
    if (!Foundation.Timer._deferralTimer) {
        Foundation.Timer._deferralTimer = new Foundation.Timer(1);
        Foundation.Timer._deferralTimer.then(function () {
            Foundation.Timer._deferralTimer = null;
        });
    }
    return Foundation.Timer._deferralTimer;
};

